'use client';

import { POPULAR_TOPICS } from '../../../lib/prompts/popular-topics.js';
import { SlackAvailabilityCallout } from '../../SlackAvailabilityCallout.js';
import type { StartScreenComposer } from './useStartScreenComposer.js';

interface ChatStartScreenCompactProps {
  locale: string;
  isLoading: boolean;
  composer: StartScreenComposer;
  showSlackCallout: boolean;
  slackCalloutMentionsSearch: boolean;
}

/** Compact start screen for Chrome sidepanel/popup hosts. */
export function ChatStartScreenCompact({
  locale,
  isLoading,
  composer,
  showSlackCallout,
  slackCalloutMentionsSearch,
}: ChatStartScreenCompactProps) {
  const { inputRef, handleSubmit, handleKeyDown, handleTopicClick } = composer;

  return (
    <div className="flex-1 overflow-y-auto overscroll-contain">
      <div className="flex flex-col px-3 py-3 min-h-full">
        <div className="mb-3">
          <h1 className="text-base font-semibold text-foreground">Start a chat</h1>
          <p className="text-xs text-muted-foreground">
            Ask Meridian about sales, operations, tickets, or follow-ups.
          </p>
        </div>

        <div className="space-y-3">
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              placeholder="Ask Meridian..."
              disabled={isLoading}
              onChange={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
              }}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-white dark:bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none rounded-md border border-input resize-none overflow-y-auto max-h-44 min-h-10"
              rows={1}
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Starting...' : 'Send'}
            </button>
          </form>

          <div>
            <p className="text-[11px] text-muted-foreground mb-2 font-medium">Quick prompts</p>
            <div className="grid grid-cols-1 gap-2">
              {POPULAR_TOPICS.slice(0, 4).map((topic) => {
                const topicLabel = locale === 'es' ? topic.labelEs : topic.label;
                const topicPrompt = locale === 'es' ? topic.optimizedPromptEs : topic.optimizedPrompt;
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => handleTopicClick(topicPrompt)}
                    className="px-3 py-2 rounded-md border border-border bg-card hover:bg-muted text-left text-xs text-foreground transition-colors"
                  >
                    {topicLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {showSlackCallout && (
            <div className="pt-2 border-t border-border">
              <SlackAvailabilityCallout variant="inline" mentionSearch={slackCalloutMentionsSearch} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
